





































































































































































































import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import { LineItem, Order } from "@/models/order/order";
import { decomposeLineNotes } from "@/models/order/line_notes";
import { NetSuiteQuoteLineItem } from "@/models/netsuite/netsuite";
import {
  DriftField,
  DriftLine,
  DriftReport,
  DriftResolution,
  QuoteDraftLine,
  toLineItem
} from "@/models/quote_draft/quote_draft";
import { GenericError } from "@/services/error_service";
import { NotificationService } from "@/services/notification_service";

/**
 * FUPOF-191: staged, VS-Code-merge-style resolution of line drift between
 * the PSQL draft and NetSuite. Selections update the UI immediately but
 * nothing is applied to the cart until "Save changes", which writes to the
 * Vuex store only — neither PSQL nor NetSuite is touched. Both are updated
 * on the next explicit save/submit.
 * Header drift is not handled here — NetSuite always wins the header and it
 * is applied automatically on reload (decision D4).
 */
@Component
export default class DriftResolver extends Vue {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetDriftReport))
  report!: DriftReport | null;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  @(namespace(StoreModule.Order).Action(OrderActions.ReplaceLineItems))
  replaceLineItems!: (value: LineItem[]) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ClearDriftReport))
  clearDriftReport!: () => Promise<void>;

  protected resolution = DriftResolution;
  protected staged: Record<string, DriftResolution> = {};
  protected saving = false;

  get driftedLines(): DriftLine[] {
    if (!this.report) return [];
    return this.report.lines.filter(
      l => l.uuid && l.drift.length > 0 && !l.drift.includes("orphan")
    );
  }

  get orphanLines(): DriftLine[] {
    if (!this.report) return [];
    return this.report.lines.filter(l => l.drift.includes("orphan"));
  }

  get removedLines(): QuoteDraftLine[] {
    return this.report?.removed_lines ?? [];
  }

  /** Every drifted field across every drifted line — the resolution total. */
  get driftFieldCount(): number {
    return this.driftedLines.reduce((total, l) => total + l.drift.length, 0);
  }

  /**
   * Counted against the current report rather than off the staged map's size,
   * so keys left behind by a previous report can't push the tally past the
   * total.
   */
  get stagedCount(): number {
    return this.driftedLines.reduce(
      (total, l) =>
        total +
        l.drift.filter(field => this.choiceFor(l, field) !== undefined).length,
      0
    );
  }

  get allStaged(): boolean {
    return this.driftedLines.every(l =>
      l.drift.every(field => this.choiceFor(l, field) !== undefined)
    );
  }

  /**
   * Explains the disabled Acknowledge button. Empty once everything is staged
   * so an enabled button doesn't carry a redundant tooltip.
   */
  get resolveTooltip(): string {
    if (this.allStaged) return "";
    const remaining = this.driftFieldCount - this.stagedCount;
    return `${this.stagedCount} of ${this.driftFieldCount} changes resolved — select an option for ${
      remaining === 1 ? "the remaining change" : `the ${remaining} remaining changes`
    } to continue.`;
  }

  /**
   * Resolutions are staged per field, so the key pairs the line with it. It
   * doubles as the radio group `name`: the Mine and NetSuite radios on a row
   * share it, which is what makes them mutually exclusive.
   */
  protected fieldKey(line: DriftLine, field: DriftField): string {
    return `${line.uuid}|${field}`;
  }

  protected choiceFor(
    line: DriftLine,
    field: DriftField
  ): DriftResolution | undefined {
    return this.staged[this.fieldKey(line, field)];
  }

  protected isAccepted(line: DriftLine, field: DriftField): boolean {
    return this.choiceFor(line, field) === DriftResolution.AcceptNs;
  }

  protected stage(
    line: DriftLine,
    field: DriftField,
    choice: DriftResolution
  ): void {
    if (!line.uuid) return;
    this.$set(this.staged, this.fieldKey(line, field), choice);
  }

  protected lineTitle(line: DriftLine): string {
    const p = line.psql_line;
    return (
      p?.display_name || p?.model || p?.sku || line.ns_line?.itemid || "Line"
    );
  }

  protected removedTitle(line: QuoteDraftLine): string {
    return line.display_name || line.model || line.sku || "Line";
  }

  /** Suppressed when the title already fell back to the SKU. */
  protected removedSku(line: QuoteDraftLine): string {
    if (!line.sku || line.sku === this.removedTitle(line)) return "";
    return line.sku;
  }

  /**
   * The SKU shown beneath the title. Suppressed when it would just repeat the
   * title (i.e. the title already fell back to the SKU).
   */
  protected lineSku(line: DriftLine): string {
    const sku = line.psql_line?.sku;
    if (!sku || sku === this.lineTitle(line)) return "";
    return sku;
  }

  /**
   * A config_uuid match means NetSuite lost the line's identity but its
   * configuration still matches one of ours — the line is recovered, not new.
   * Labelling it "Added in NetSuite" would tell the user a line appeared that
   * they in fact created themselves.
   */
  protected isRecovered(line: DriftLine): boolean {
    return line.match === "config_uuid";
  }

  protected orphanTitle(line: DriftLine): string {
    return (
      line.ns_line?.displayname ||
      line.ns_line?.custcol_custom_sku ||
      line.ns_line?.itemid ||
      line.psql_line?.display_name ||
      "NetSuite line"
    );
  }

  /** Suppressed when the title already fell back to the SKU, as on removed lines. */
  protected orphanSku(line: DriftLine): string {
    const sku =
      line.ns_line?.custcol_custom_sku ||
      line.ns_line?.itemid ||
      line.psql_line?.sku ||
      "";
    return sku && sku !== this.orphanTitle(line) ? sku : "";
  }

  protected orphanQty(line: DriftLine): number | null {
    const qty = line.ns_line?.quantity;
    return qty != null ? Number(qty) : null;
  }

  /**
   * NetSuite reports both a base price and a (possibly discounted) rate; the
   * base is what the rest of the app treats as the line's unit price.
   */
  protected orphanUnitPrice(line: DriftLine): number | null {
    const ns = line.ns_line;
    if (!ns) return null;
    const price = ns.base_unit_price != null ? ns.base_unit_price : ns.rate;
    return price != null ? Number(price) : null;
  }

  /** Extended price for the row — null whenever either half is unknown. */
  protected orphanTotal(line: DriftLine): number | null {
    const qty = this.orphanQty(line);
    const price = this.orphanUnitPrice(line);
    if (qty === null || price === null) return null;
    return qty * price;
  }

  protected removedTotal(line: QuoteDraftLine): number | null {
    if (line.qty == null || line.unit_price == null) return null;
    return Number(line.qty) * Number(line.unit_price);
  }

  /** How this line came to be here, when there's something worth saying. */
  protected orphanNote(line: DriftLine): string {
    if (this.isRecovered(line)) {
      return "Matched to one of your lines by its configuration.";
    }
    if (line.psql_line?.parsed) {
      return "Configuration reconstructed from SKU (best effort).";
    }
    return "";
  }

  /**
   * The global `currency` filter isn't reachable from a template expression
   * building an interpolated string, so the same formatting is exposed here.
   */
  protected money(value: number | null): string {
    if (value == null) return "?";
    return (Math.round(value * 100) / 100)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  protected fieldLabel(field: DriftField): string {
    return field === "unit_price" ? "price" : field;
  }

  protected mineValue(line: DriftLine, field: DriftField): string {
    const p = line.psql_line;
    if (!p) return "—";
    switch (field) {
      case "sku":
        return p.sku ?? "—";
      case "qty":
        return String(p.qty);
      case "unit_price":
        return `$${p.unit_price}`;
      case "multiplier":
        return String(p.multiplier);
      case "notes":
        return p.notes || "—";
      default:
        return "—";
    }
  }

  /**
   * The line's production notes. This — not `memo`/`displayname`, which carry
   * the item's description — is the field the API drift-checks against the
   * draft's `notes`. NetSuite types the column loosely, hence the coercion.
   */
  protected nsProductionNotes(ns: NetSuiteQuoteLineItem): string {
    const raw = ns.custcol_productionnotes;
    return raw == null ? "" : String(raw);
  }

  protected nsValue(line: DriftLine, field: DriftField): string {
    const ns = line.ns_line;
    if (!ns) return "—";
    switch (field) {
      case "sku":
        // The API now resolves the SKU into `sku`; fall back to the legacy
        // custom-SKU/item-name fields for pre-contract responses.
        return ns.sku ?? ns.custcol_custom_sku ?? ns.itemid ?? "—";
      case "qty":
        return String(ns.quantity);
      case "unit_price":
        // `rate` bakes in the multiplier; `base_unit_price` is the pre-multiplier
        // figure the cart expects. Prefer it, fall back to `rate`.
        return `$${ns.base_unit_price != null ? ns.base_unit_price : ns.rate}`;
      case "multiplier":
        return ns.multiplier != null ? String(ns.multiplier) : "—";
      case "notes":
        return this.nsProductionNotes(ns) || "—";
      default:
        return "—";
    }
  }

  /**
   * Applies the staged resolutions to the cart and clears the drift report.
   * Vuex only — the PSQL draft and NetSuite are deliberately left untouched
   * so the two can never diverge: they are both written, together, by the
   * next explicit submit.
   */
  protected async saveChanges(): Promise<void> {
    if (!this.report || !this.allStaged) return;
    this.saving = true;
    try {
      // Lines deleted in NetSuite are informational (not a resolvable
      // drift), but they must leave the local quote too — otherwise the
      // next submit would silently re-add them to NS.
      const removedUuids = new Set(
        this.removedLines.map(l => l.uuid).filter(Boolean)
      );
      const items: LineItem[] = [
        ...(this.storeOrder.items ?? [])
      ].filter(item => !item.uuid || !removedUuids.has(item.uuid));
      const byUuid = new Map<string, number>();
      items.forEach((item, i) => {
        if (item.uuid) byUuid.set(item.uuid, i);
      });

      // Each drifted field is resolved on its own, so only the fields staged
      // Accept-NS are overwritten — a field left on Keep-mine keeps my value
      // even when its neighbours on the same line took NetSuite's.
      for (const line of this.driftedLines) {
        if (!line.uuid || !line.ns_line) continue;
        const idx = byUuid.get(line.uuid);
        if (idx === undefined) continue;
        const nsLine = line.ns_line;
        // Accepting the SKU means my configuration no longer describes the
        // line, so it is dropped below and the line locks to qty/price/notes.
        const skuAccepted = this.isAccepted(line, "sku");
        const item: LineItem = { ...items[idx] };

        if (this.isAccepted(line, "qty")) item.qty = nsLine.quantity;

        if (this.isAccepted(line, "unit_price")) {
          // `rate` bakes in the multiplier; the cart multiplies again, so store
          // the pre-multiplier `base_unit_price` (fall back to `rate`).
          item.unit_price =
            nsLine.base_unit_price != null
              ? nsLine.base_unit_price
              : nsLine.rate;
        }

        if (this.isAccepted(line, "multiplier") && nsLine.multiplier != null) {
          item.multiplier = nsLine.multiplier;
          // Pin it: the cart re-asserts the quote-wide price level over every
          // line on mount, which would otherwise revert this on the way back.
          item.ns_multiplier = true;
        }

        if (this.isAccepted(line, "notes")) {
          // NetSuite holds the notes flattened into one string; the cart holds
          // them split between free text and the configurator's per-field
          // notes, so take the string apart to accept both halves.
          const nsNotes = decomposeLineNotes(this.nsProductionNotes(nsLine));
          item.notes = nsNotes.freeText;
          // A dropped configuration takes its field notes with it.
          if (!skuAccepted && item.item_details) {
            item.item_details = {
              ...item.item_details,
              notes: nsNotes.fieldNotes
            } as LineItem["item_details"];
          }
        }

        if (skuAccepted) {
          item.sku = nsLine.sku ?? nsLine.custcol_custom_sku ?? item.sku;
          item.item_details = undefined;
          item.ns_locked = true;
        }

        items[idx] = item;
      }

      // Orphans: lines added directly in NetSuite become part of the quote.
      // When the SKU parser reconstructed a configuration the line stays
      // reconfigurable (plan: "Try reconfiguring this line"); otherwise it
      // locks to qty/price/notes edits.
      for (const line of this.orphanLines) {
        if (line.psql_line) {
          items.push({
            ...toLineItem(line.psql_line),
            ns_locked: !line.psql_line.item_details
          });
        }
      }

      // Removed lines only exist in the report, not in the hydrated cart, so
      // there is nothing to strip locally — they're informational.

      await this.replaceLineItems(items);
      await this.clearDriftReport();
      NotificationService.show("Quote changes applied.", "success");
    } catch (err) {
      GenericError.popup(err.message);
    } finally {
      this.saving = false;
    }
  }
}
